import type { EventCategory, EventCategoryCreateInput } from "@cdt/shared";
import { OUTLOOK_CATEGORY_COLORS } from "@cdt/shared";
import { useState } from "react";

import { CategoriesTable } from "@/components/categories/CategoriesTable";
import { DeleteCategoryDialog } from "@/components/categories/DeleteCategoryDialog";
import { EventsErrorState } from "@/components/events/EventsErrorState";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  useCreateCategory,
  useDeleteCategory,
  useUpdateCategory,
} from "@/hooks/useCategoryActions";
import { useCategoriesQuery } from "@/hooks/useCategoriesQuery";
import { ApiError } from "@/lib/api";

export function CategoriesPage() {
  const query = useCategoriesQuery();
  const createMutation = useCreateCategory();
  const updateMutation = useUpdateCategory();
  const deleteMutation = useDeleteCategory();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState<string>("");
  const [editing, setEditing] = useState<EventCategory | null>(null);
  const [forceDeleteTarget, setForceDeleteTarget] = useState<{
    item: EventCategory;
    usageCount: number;
  } | null>(null);

  const isInitialLoading = query.isLoading && query.data === undefined;

  function resetForm() {
    setName("");
    setDescription("");
    setColor("");
    setEditing(null);
  }

  async function submit(): Promise<void> {
    const payload: EventCategoryCreateInput = {
      name: name.trim(),
      ...(description.trim() ? { description: description.trim() } : {}),
      ...(color ? { outlookColor: color as EventCategoryCreateInput["outlookColor"] } : {}),
    };
    if (editing) {
      await updateMutation.mutateAsync({
        id: editing.id,
        patch: {
          name: payload.name,
          description: description.trim() || null,
          ...(payload.outlookColor ? { outlookColor: payload.outlookColor } : {}),
        },
      });
    } else {
      await createMutation.mutateAsync(payload);
    }
    resetForm();
  }

  async function onDelete(item: EventCategory): Promise<void> {
    try {
      await deleteMutation.mutateAsync({ id: item.id });
      if (editing?.id === item.id) resetForm();
    } catch (error) {
      const usageCount =
        error instanceof ApiError && error.status === 409
          ? (error.body as { usageCount?: number } | null)?.usageCount
          : undefined;
      if (usageCount !== undefined) {
        setForceDeleteTarget({ item, usageCount });
      }
    }
  }

  async function onConfirmForceDelete(
    reassignToId: string | undefined,
  ): Promise<void> {
    if (!forceDeleteTarget) return;
    const { item } = forceDeleteTarget;
    await deleteMutation.mutateAsync({ id: item.id, force: true, reassignToId });
    setForceDeleteTarget(null);
    if (editing?.id === item.id) resetForm();
  }

  return (
    <div className="container space-y-6 py-8">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Categorie</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Gestisci categorie applicative sincronizzate con Outlook.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {editing ? "Modifica categoria" : "Nuova categoria"}
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="category-name">Nome</Label>
            <Input
              id="category-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Es. Musica"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="category-color">Colore Outlook (opzionale)</Label>
            <select
              id="category-color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-10 w-full rounded-md border bg-background px-3 text-sm"
            >
              <option value="">Auto (primo disponibile)</option>
              {OUTLOOK_CATEGORY_COLORS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2 md:col-span-3">
            <Label htmlFor="category-description">Descrizione per l&apos;AI (opzionale)</Label>
            <Textarea
              id="category-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Es. Comunicati istituzionali e informazioni ufficiali da enti pubblici"
              rows={2}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground">
              Aiuta l&apos;AI a scegliere la categoria giusta, anche in base al mittente o al dominio.
            </p>
          </div>
          <div className="md:col-span-3 flex gap-2">
            <Button
              type="button"
              onClick={() => void submit()}
              disabled={
                name.trim().length === 0 ||
                createMutation.isPending ||
                updateMutation.isPending
              }
            >
              {editing ? "Salva" : "Crea categoria"}
            </Button>
            {editing ? (
              <Button type="button" variant="outline" onClick={resetForm}>
                Annulla modifica
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {isInitialLoading ? (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">
            Caricamento categorie...
          </CardContent>
        </Card>
      ) : query.isError ? (
        <EventsErrorState
          message={query.error instanceof Error ? query.error.message : undefined}
          onRetry={() => void query.refetch()}
        />
      ) : query.data && query.data.items.length > 0 ? (
        <CategoriesTable
          items={query.data.items}
          deletingId={deleteMutation.isPending ? (deleteMutation.variables?.id ?? null) : null}
          onEdit={(item) => {
            setEditing(item);
            setName(item.name);
            setDescription(item.description ?? "");
            setColor(item.outlookColor);
          }}
          onDelete={(item) => void onDelete(item)}
        />
      ) : (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">
            Nessuna categoria configurata.
          </CardContent>
        </Card>
      )}

      {forceDeleteTarget ? (
        <DeleteCategoryDialog
          categoryName={forceDeleteTarget.item.name}
          usageCount={forceDeleteTarget.usageCount}
          otherCategories={
            query.data?.items.filter(
              (c) => c.id !== forceDeleteTarget.item.id,
            ) ?? []
          }
          open={forceDeleteTarget !== null}
          onOpenChange={(open) => {
            if (!open) setForceDeleteTarget(null);
          }}
          onConfirm={(reassignToId) => void onConfirmForceDelete(reassignToId)}
          pending={deleteMutation.isPending}
        />
      ) : null}
    </div>
  );
}
